import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { 
  Search, MapPin, Utensils, Bed, Car, Wrench, Home, Heart,
  Briefcase, GraduationCap, Stethoscope, Anchor, Plane, 
  ShoppingBag, Dumbbell, Sparkles, Theater, Building, Laptop,
  PawPrint, Flower, PartyPopper, ChevronRight, Star, TrendingUp
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import LiveSearch from '../../components/LiveSearch';
import { 
  AccommodationBookingWidget, 
  CarHireBookingWidget, 
  ToursActivitiesWidget 
} from '../../components/AffiliateWidgets';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Iconic Australian Attractions - Featured Hotspots with Images
const ICONIC_ATTRACTIONS = [
  // Gold Coast
  { name: 'Surfers Paradise', city: 'gold-coast', cityName: 'Gold Coast', size: 'xl', image: 'https://images.unsplash.com/photo-1671418087163-9a29e29caefa?w=400&h=400&fit=crop', category: 'tourism' },
  { name: 'Theme Parks', city: 'gold-coast', cityName: 'Gold Coast', size: 'lg', image: 'https://images.unsplash.com/photo-1622058014838-2a354fc585a5?w=400&h=400&fit=crop', category: 'tourism' },
  // Sydney
  { name: 'Harbour Bridge', city: 'sydney', cityName: 'Sydney', size: 'xl', image: 'https://images.unsplash.com/photo-1599617491715-6a27ca6f1b15?w=400&h=400&fit=crop', category: 'tourism' },
  { name: 'Luna Park', city: 'sydney', cityName: 'Sydney', size: 'xl', image: 'https://images.pexels.com/photos/7276595/pexels-photo-7276595.jpeg?auto=compress&w=400&h=400&fit=crop', category: 'tourism' },
  { name: 'Opera House', city: 'sydney', cityName: 'Sydney', size: 'lg', image: 'https://images.unsplash.com/photo-1513315911826-0d184239bc7f?w=400&h=400&fit=crop', category: 'tourism' },
  // Melbourne
  { name: 'F1 Grand Prix', city: 'melbourne', cityName: 'Melbourne', size: 'xl', image: 'https://images.unsplash.com/photo-1724461186447-4b524fd4557e?w=400&h=400&fit=crop', category: 'tourism' },
  { name: 'Melbourne Laneways', city: 'melbourne', cityName: 'Melbourne', size: 'lg', image: 'https://images.unsplash.com/photo-1545044846-351ba102b6d5?w=400&h=400&fit=crop', category: 'tourism' },
  // Perth
  { name: 'Elizabeth Quay', city: 'perth', cityName: 'Perth', size: 'lg', image: 'https://images.unsplash.com/photo-1579954188748-4c1c5b8e7a4c?w=400&h=400&fit=crop', category: 'tourism' },
  { name: 'Margaret River', city: 'perth', cityName: 'Perth', size: 'lg', image: 'https://images.unsplash.com/photo-1736333568797-48339dc1b042?w=400&h=400&fit=crop', category: 'tourism' },
  // Adelaide
  { name: 'Barossa Valley', city: 'adelaide', cityName: 'Adelaide', size: 'lg', image: 'https://images.unsplash.com/photo-1664970925595-90402bf3fbcb?w=400&h=400&fit=crop', category: 'tourism' },
  { name: 'Adelaide Oval', city: 'adelaide', cityName: 'Adelaide', size: 'md', image: 'https://images.unsplash.com/photo-1540747913346-19e32dc3e97e?w=400&h=400&fit=crop', category: 'tourism' },
  // Cairns
  { name: 'Great Barrier Reef', city: 'cairns', cityName: 'Cairns', size: 'xl', image: 'https://images.unsplash.com/photo-1745917784380-6f423e4bea8e?w=400&h=400&fit=crop', category: 'tourism' },
  { name: 'Daintree Rainforest', city: 'cairns', cityName: 'Cairns', size: 'lg', image: 'https://images.unsplash.com/photo-1518531933037-91b2f5f229cc?w=400&h=400&fit=crop', category: 'tourism' },
  // Sunshine Coast
  { name: 'Australia Zoo', city: 'sunshine-coast', cityName: 'Sunshine Coast', size: 'lg', image: 'https://images.unsplash.com/photo-1673418064239-4ecfa5956691?w=400&h=400&fit=crop', category: 'tourism' },
  { name: 'Noosa Heads', city: 'sunshine-coast', cityName: 'Sunshine Coast', size: 'md', image: 'https://images.unsplash.com/photo-1686804294556-7bd6c60b2a96?w=400&h=400&fit=crop', category: 'tourism' },
  // Canberra
  { name: 'Parliament House', city: 'canberra', cityName: 'Canberra', size: 'lg', image: 'https://images.unsplash.com/photo-1672264597620-d792bb6de88d?w=400&h=400&fit=crop', category: 'tourism' },
  { name: 'War Memorial', city: 'canberra', cityName: 'Canberra', size: 'md', image: 'https://images.unsplash.com/photo-1672265256655-3bc5c1b1424d?w=400&h=400&fit=crop', category: 'tourism' },
  // Darwin
  { name: 'Aboriginal Art', city: 'darwin', cityName: 'Darwin', size: 'lg', image: 'https://images.unsplash.com/photo-1685344967764-785f77cba460?w=400&h=400&fit=crop', category: 'tourism' },
  { name: 'Kakadu', city: 'darwin', cityName: 'Darwin', size: 'lg', image: 'https://images.unsplash.com/photo-1696583867494-9c15695edffe?w=400&h=400&fit=crop', category: 'tourism' },
  // Brisbane
  { name: 'South Bank', city: 'brisbane', cityName: 'Brisbane', size: 'md', image: 'https://images.unsplash.com/photo-1565299624946-b28f40a0ae38?w=400&h=400&fit=crop', category: 'tourism' },
  { name: 'Story Bridge', city: 'brisbane', cityName: 'Brisbane', size: 'md', image: 'https://images.unsplash.com/photo-1545044846-351ba102b6d5?w=400&h=400&fit=crop', category: 'tourism' },
];

// Bubble size classes
const bubbleSizes = {
  sm: 'w-20 h-20 text-[10px]',
  md: 'w-24 h-24 text-xs',
  lg: 'w-32 h-32 text-sm',
  xl: 'w-40 h-40 text-base',
};

// Icon mapping for categories
const categoryIcons = {
  'accommodation': Bed,
  'food-dining': Utensils,
  'arts-entertainment': Theater,
  'sporting-leisure': Dumbbell,
  'health-beauty': Sparkles,
  'automotive': Car,
  'shopping': ShoppingBag,
  'services': Wrench,
  'tradesman': Wrench,
  'business-finance': Briefcase,
  'builders': Building,
  'boats-boating': Anchor,
  'weddings': Heart,
  'party-hire': PartyPopper,
  'car-hire': Car,
  'it-web': Laptop,
  'office-services': Building,
  'real-estate': Home,
  'education': GraduationCap,
  'pets': PawPrint,
  'home-garden': Flower,
  'transport-travel': Plane,
  'tourism': MapPin,
  'medical': Stethoscope,
};

const HotspotsHome = () => {
  const [config, setConfig] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const response = await fetch(`${API_URL}/api/hotspots/config`);
      const data = await response.json();
      setConfig(data);
    } catch (error) {
      console.error('Error fetching config:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      window.location.href = `/hotspots/search?q=${encodeURIComponent(searchQuery)}`;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-sky-50 via-white to-orange-50 flex items-center justify-center">
        <div className="animate-pulse text-sky-600 text-xl">Loading Australian Hotspots...</div>
      </div>
    );
  }

  const locations = config?.locations || [];
  const categories = config?.categories || [];

  return (
    <div className="min-h-screen bg-gradient-to-br from-sky-50 via-white to-orange-50">
      <Helmet>
        <title>Australian Hotspots | Discover Local Businesses Across Australia</title>
        <meta name="description" content="Discover the best local businesses across Australia. Restaurants, cafes, attractions, services and more in 10 major cities. Your guide to Australian hotspots." />
      </Helmet>
      
      {/* Hero Section */}
      <section className="relative overflow-hidden min-h-[600px]">
        {/* Hero Background Image */}
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1523482580672-f109ba8cb9be?auto=format&fit=crop&w=2000&q=80" 
            alt="Australian coastline" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/70"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          {/* Main Headline - H1 */}
          <div className="text-center mb-8">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 text-white leading-tight">
              Welcome to <span className="text-sky-400">Australian Hotspots</span>
              <br />
              <span className="text-3xl sm:text-4xl lg:text-5xl font-semibold text-orange-400">The Premier Travel & Lifestyle Directory</span>
            </h1>
            
            {/* Sub-headline */}
            <p className="text-xl sm:text-2xl text-white/90 font-medium mb-6 max-w-3xl mx-auto">
              Find exactly where to <span className="text-sky-300">See</span>, <span className="text-emerald-300">Visit</span>, <span className="text-orange-300">Play</span>, and <span className="text-pink-300">Stay</span> anywhere in Australia.
            </p>
            
            {/* Body Text */}
            <p className="text-lg text-white/80 max-w-4xl mx-auto mb-10 leading-relaxed">
              Planning your next adventure? Australian Hotspots connects you with top-rated businesses, spectacular sights, and unbeatable accommodations across every Australian state and major city. Explore our curated directory to find trusted recommendations for dining, entertainment, sightseeing, and lodging. Your ultimate Australian itinerary is just a mouse click away.
            </p>
          </div>

          {/* Search Box - Prominent */}
          <div className="max-w-4xl mx-auto mb-8">
            <div className="p-3 bg-white rounded-2xl shadow-2xl overflow-visible transform hover:scale-[1.01] transition-transform">
              <LiveSearch 
                type="hotspots"
                placeholder="Search destinations, restaurants, hotels, attractions..."
                className="w-full text-lg"
              />
            </div>
          </div>
          
          {/* CTA Button */}
          <div className="text-center mb-12">
            <button 
              onClick={() => document.querySelector('[data-testid="live-search-input"]')?.focus()}
              className="inline-flex items-center gap-2 bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white font-bold text-lg px-8 py-4 rounded-full shadow-xl hover:shadow-2xl transition-all transform hover:scale-105"
            >
              <Search className="w-5 h-5" />
              Search Destinations Now
            </button>
          </div>

          {/* Quick Stats */}
          <div className="flex flex-wrap justify-center gap-6 text-center">
            <div className="bg-white/80 backdrop-blur-sm rounded-xl px-6 py-4 shadow-lg">
              <div className="text-3xl font-bold text-sky-600">{locations.length}</div>
              <div className="text-sm text-slate-600">Major Cities</div>
            </div>
            <div className="bg-white/80 backdrop-blur-sm rounded-xl px-6 py-4 shadow-lg">
              <div className="text-3xl font-bold text-orange-500">{categories.length}</div>
              <div className="text-sm text-slate-600">Categories</div>
            </div>
            <div className="bg-white/80 backdrop-blur-sm rounded-xl px-6 py-4 shadow-lg">
              <div className="text-3xl font-bold text-emerald-500">10K+</div>
              <div className="text-sm text-slate-600">Businesses</div>
            </div>
          </div>
        </div>
      </section>

      {/* Iconic Australian Attractions - Grid Layout */}
      <section className="py-16 bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-3">
              Iconic Australian Experiences
            </h2>
            <p className="text-lg text-slate-400">
              Click to explore world-famous destinations
            </p>
          </div>

          {/* Grid Layout for Attractions */}
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-3 md:gap-4">
            {ICONIC_ATTRACTIONS.map((attraction, index) => {
              return (
                <Link
                  key={`${attraction.city}-${index}`}
                  to={`/hotspots/${attraction.city}/${attraction.category}`}
                  className="aspect-square rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transform hover:scale-105 transition-all duration-300 cursor-pointer group relative"
                  data-testid={`attraction-bubble-${attraction.city}`}
                >
                  {/* Background Image */}
                  <div 
                    className="absolute inset-0 bg-cover bg-center"
                    style={{ backgroundImage: `url(${attraction.image})` }}
                  />
                  {/* Dark overlay for text readability */}
                  <div className="absolute inset-0 bg-black/40 group-hover:bg-black/30 transition-colors" />
                  {/* Text content */}
                  <div className="absolute inset-0 flex flex-col items-center justify-center text-white p-2">
                    <span className="font-bold text-sm sm:text-base leading-tight drop-shadow-lg text-center">
                      {attraction.name}
                    </span>
                    <span className="text-xs mt-1 opacity-90 group-hover:opacity-100 drop-shadow">
                      {attraction.cityName}
                    </span>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Explore Cities Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Explore Australian Cities
            </h2>
            <p className="text-lg text-slate-600">
              From tropical Cairns to cosmopolitan Melbourne
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {locations.map((location) => (
              <Link
                key={location.id}
                to={`/hotspots/${location.id}`}
                className="group relative overflow-hidden rounded-2xl aspect-[4/3] bg-gradient-to-br from-sky-400 to-sky-600 hover:from-sky-500 hover:to-sky-700 transition-all duration-300 shadow-lg hover:shadow-xl hover:-translate-y-1"
                data-testid={`city-card-${location.id}`}
              >
                <div className="absolute inset-0 bg-black/20 group-hover:bg-black/30 transition-colors" />
                <div className="absolute inset-0 p-4 flex flex-col justify-end text-white">
                  <div className="flex items-center gap-1 text-xs opacity-80 mb-1">
                    <MapPin className="h-3 w-3" />
                    {location.state}
                  </div>
                  <h3 className="font-bold text-lg leading-tight">{location.name}</h3>
                  <p className="text-xs opacity-80 mt-1 line-clamp-1">{location.tagline}</p>
                </div>
                <ChevronRight className="absolute top-4 right-4 h-5 w-5 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* AFFILIATE BOOKING SECTION - Plan Your Australian Trip */}
      <section className="py-16 bg-gradient-to-b from-slate-100 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-3">
              Plan Your Australian Adventure
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Book accommodation, hire a car, and find amazing tours & activities. 
              All from our trusted travel partners.
            </p>
          </div>
          
          {/* Full-width Accommodation Widget */}
          <div className="mb-6">
            <AccommodationBookingWidget cityId="australia" cityName="Australia" variant="full" />
          </div>
          
          {/* Car Hire and Tours side by side */}
          <div className="grid md:grid-cols-2 gap-6">
            <CarHireBookingWidget cityId="australia" cityName="Australia" variant="full" />
            <ToursActivitiesWidget cityId="australia" cityName="Australia" variant="full" />
          </div>
        </div>
      </section>

      {/* Browse Categories Section */}
      <section className="py-20 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Browse by Category
            </h2>
            <p className="text-lg text-slate-600">
              Find exactly what you're looking for
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {categories.map((category) => {
              const IconComponent = categoryIcons[category.id] || MapPin;
              return (
                <Link
                  key={category.id}
                  to={`/hotspots/category/${category.slug}`}
                  className="group flex flex-col items-center p-6 bg-white rounded-xl border border-slate-200 hover:border-sky-300 hover:shadow-lg transition-all duration-300"
                  data-testid={`category-card-${category.id}`}
                >
                  <div className="w-14 h-14 rounded-full bg-sky-100 group-hover:bg-sky-500 flex items-center justify-center mb-3 transition-colors">
                    <IconComponent className="h-6 w-6 text-sky-600 group-hover:text-white transition-colors" />
                  </div>
                  <span className="text-sm font-medium text-slate-700 text-center leading-tight">
                    {category.name}
                  </span>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Featured Locations Spotlight */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Popular Destinations
            </h2>
            <p className="text-lg text-slate-600">
              Most searched locations this month
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {locations.slice(0, 3).map((location, index) => (
              <Link
                key={location.id}
                to={`/hotspots/${location.id}`}
                className="group relative overflow-hidden rounded-2xl aspect-video bg-gradient-to-br from-slate-800 to-slate-900 shadow-xl hover:shadow-2xl transition-all duration-300"
                data-testid={`trending-card-${location.id}`}
              >
                {/* Background gradient overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-black/20 z-[1]" />
                
                {/* Trending Badge - Top Left */}
                <div className="absolute top-4 left-4 z-[3]">
                  <div className="bg-orange-500 text-white text-xs font-bold px-3 py-1.5 rounded-full flex items-center gap-1 shadow-lg">
                    <TrendingUp className="h-3 w-3" />
                    <span>#{index + 1} Trending</span>
                  </div>
                </div>
                
                {/* Content - Bottom */}
                <div className="absolute bottom-0 left-0 right-0 p-5 z-[2]">
                  <div className="flex items-center gap-2 mb-1">
                    <MapPin className="h-3 w-3 text-sky-400" />
                    <span className="text-sky-400 text-xs">{location.state_full}</span>
                  </div>
                  <h3 className="font-bold text-xl mb-1 text-white">{location.name}</h3>
                  <p className="text-slate-300 text-xs mb-3 line-clamp-2">{location.description}</p>
                  <div className="flex flex-wrap gap-1.5">
                    {location.sub_locations?.slice(0, 3).map((sub) => (
                      <span key={sub.slug} className="text-xs bg-white/20 backdrop-blur-sm px-2 py-0.5 rounded-full text-white">
                        {sub.name}
                      </span>
                    ))}
                    {location.sub_locations?.length > 3 && (
                      <span className="text-xs bg-white/20 backdrop-blur-sm px-2 py-0.5 rounded-full text-white">
                        +{location.sub_locations.length - 3} more
                      </span>
                    )}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* AI Concierge Teaser */}
      <section className="py-20 bg-gradient-to-r from-sky-600 to-sky-700">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm rounded-full px-4 py-2 mb-6">
            <Star className="h-4 w-4 text-yellow-300" />
            <span className="text-sm font-medium">Coming Soon</span>
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold mb-4">
            Meet Kata - Your AI Travel Concierge
          </h2>
          <p className="text-xl text-sky-100 mb-8">
            "Plan my Gold Coast trip for a family of 4" - Just ask, and Kata will create your perfect itinerary
          </p>
          <Button
            size="lg"
            variant="secondary"
            className="bg-white text-sky-600 hover:bg-sky-50"
            disabled
          >
            Ask Kata (Coming Soon)
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8 mb-12">
            <div>
              <h3 className="text-xl font-bold mb-4">
                <span className="text-sky-400">Australian</span>
                <span className="text-orange-400"> Hotspots</span>
              </h3>
              <p className="text-slate-400 text-sm">
                Discover Australia's best local businesses, from accommodation to dining, services and more.
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Popular Cities</h4>
              <ul className="space-y-2 text-sm text-slate-400">
                {locations.slice(0, 5).map((loc) => (
                  <li key={loc.id}>
                    <Link to={`/hotspots/${loc.id}`} className="hover:text-white transition-colors">
                      {loc.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Categories</h4>
              <ul className="space-y-2 text-sm text-slate-400">
                {categories.slice(0, 5).map((cat) => (
                  <li key={cat.id}>
                    <Link to={`/hotspots/category/${cat.slug}`} className="hover:text-white transition-colors">
                      {cat.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">For Business</h4>
              <ul className="space-y-2 text-sm text-slate-400">
                <li><Link to="/hotspots/advertise" className="hover:text-white transition-colors">List Your Business</Link></li>
                <li><Link to="/hotspots/advertise" className="hover:text-white transition-colors">Advertising</Link></li>
                <li><a href="#" className="hover:text-white transition-colors">Partner With Us</a></li>
              </ul>
            </div>
          </div>
          
          {/* Lionheart Digital Branding - Minimal */}
          <div className="border-t border-slate-800 pt-8">
            {/* Main Branding Block */}
            <div className="flex flex-col items-center text-center mb-4">
              {/* Lion Logo - Matched to text */}
              <div className="relative mb-2">
                <img 
                  src="https://static.prod-images.emergentagent.com/jobs/28a7e231-ff1b-4b8f-915f-47798802b078/images/2fa9ddf7953c616448f48ed011e225e9e3469523a5a5c8f53c562308f1d829cc.png" 
                  alt="Lionheart Digital" 
                  className="h-12 md:h-14 w-auto relative z-10"
                />
              </div>
              
              {/* Brand Name - Small */}
              <h3 className="text-sm md:text-base font-semibold tracking-tight mb-1">
                <span className="text-white">LIONHEART</span>
                <span className="text-orange-500"> DIGITAL</span>
              </h3>
              
              {/* The Statement - Tiny */}
              <p className="text-xs text-slate-500 italic">
                "They lit the fire. We became it."
              </p>
            </div>
            
            {/* Copyright */}
            <div className="text-center text-sm text-slate-500 border-t border-slate-800 pt-6">
              <p>&copy; {new Date().getFullYear()} Australian Hotspots. A Lionheart Digital Asset.</p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HotspotsHome;
